import { apiClient } from './apiClient';
import { ProductReturn, CreateReturnRequest } from '../types';

interface ReturnListResponse {
  success: boolean;
  data: ProductReturn[];
  total: number;
  current_page: number;
  last_page: number;
  per_page: number;
}

export const productReturnService = {
  /**
   * Get all product returns
   */
  async getReturns(
    filters?: {
      status?: string;
      search?: string;
    },
    page: number = 1,
    perPage: number = 15
  ): Promise<ReturnListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    if (filters?.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }

    const response = await apiClient.get<ReturnListResponse>(
      `/returns?${params.toString()}`
    );
    return response;
  },

  /**
   * Get a specific return
   */
  async getReturn(id: number): Promise<ProductReturn> {
    const response = await apiClient.get<{ data: ProductReturn }>(
      `/returns/${id}`
    );
    return response.data;
  },

  /**
   * Create a new product return
   */
  async createReturn(data: CreateReturnRequest): Promise<ProductReturn> {
    const response = await apiClient.post<{ data: ProductReturn }>(
      '/returns',
      data
    );
    return response.data;
  },

  /**
   * Approve a return
   */
  async approveReturn(id: number): Promise<ProductReturn> {
    const response = await apiClient.post<{ data: ProductReturn }>(
      `/returns/${id}/approve`,
      {}
    );
    return response.data;
  },

  /**
   * Reject a return
   */
  async rejectReturn(
    id: number,
    rejectionReason: string
  ): Promise<ProductReturn> {
    const response = await apiClient.post<{ data: ProductReturn }>(
      `/returns/${id}/reject`,
      { rejection_reason: rejectionReason }
    );
    return response.data;
  },

  /**
   * Process a return (restore stock)
   */
  async processReturn(id: number): Promise<ProductReturn> {
    const response = await apiClient.post<{ data: ProductReturn }>(
      `/returns/${id}/process`,
      {}
    );
    return response.data;
  },

  /**
   * Delete a return (only pending)
   */
  async deleteReturn(id: number): Promise<void> {
    await apiClient.delete(`/returns/${id}`);
  },
};

export default productReturnService;
